<template>
  <div class="streaming-wrapper">
    <!-- Verificando sesión -->
    <div
      v-if="verificando"
      class="d-flex justify-content-center align-items-center"
      style="min-height: 100vh"
    >
      <div class="spinner-border text-light" role="status"></div>
    </div>

    <!-- Sesión válida -->
    <template v-else>
      <!-- Header -->
      <header
        class="custom-header d-flex justify-content-between align-items-center px-4 py-3 shadow-sm"
      >
        <div class="d-flex align-items-center gap-2">
          <img :src="logoEGPro" alt="Logo Evolve Grappling Pro" class="header-logo" />
        </div>
        <button class="btn btn-outline-light btn-sm" @click="cerrarSesion">
          Cerrar sesión
        </button>
      </header>

      <!-- Contenido principal -->
      <main class="container text-white text-center content-main">
        <!-- Evento aún no comienza -->
        <div v-if="!cargandoEvento && !eventoEnVivo" class="pre-evento-card mx-auto">
          <div class="pre-evento-icon">
            <i class="bi bi-broadcast"></i>
          </div>
          <h4 class="fw-bold mb-2 text-light-50">La transmisión aún no ha comenzado</h4>
          <p class="text-light-50 mb-4">
            Tu acceso está confirmado. El evento en vivo
            <strong>{{ eventoData?.nombre || "Evolve Grappling Pro" }}</strong>
            comenzará su transmisión el
            <strong>{{ fechaFormateada }}</strong>
            <template v-if="horaFormateada">
              a las <strong>{{ horaFormateada }}</strong></template
            >.
          </p>

          <div v-if="mostrarCountdown" class="countdown-grid">
            <div class="countdown-item">
              <span class="countdown-num">{{ countdown.dias }}</span>
              <span class="countdown-label">días</span>
            </div>
            <div class="countdown-item">
              <span class="countdown-num">{{ countdown.horas }}</span>
              <span class="countdown-label">hrs</span>
            </div>
            <div class="countdown-item">
              <span class="countdown-num">{{ countdown.minutos }}</span>
              <span class="countdown-label">min</span>
            </div>
            <div class="countdown-item">
              <span class="countdown-num">{{ countdown.segundos }}</span>
              <span class="countdown-label">seg</span>
            </div>
          </div>

          <p class="text-light-50 small mt-4 mb-0">
            No es necesario hacer nada más: solo vuelve a esta misma página cuando
            comience el evento y tu transmisión estará disponible automáticamente.
          </p>
        </div>

        <!-- Evento ya empezó, pero la señal real aún no llega: comercial de espera -->
        <div v-else-if="!cargandoEvento && eventoEnVivo && !transmisionEnVivo">
          <div class="live-badge mb-3 badge-espera">
            <span class="live-dot"></span>
            LA TRANSMISIÓN INICIARÁ EN BREVE
          </div>
          <div class="video-container ratio ratio-16x9 mb-4">
            <iframe
              src="https://www.youtube.com/embed/Ndi4kjk_ImM?rel=0&autoplay=1&mute=1&loop=1&playlist=Ndi4kjk_ImM"
              title="Evolve Grappling Pro"
              allowfullscreen
            ></iframe>
          </div>
        </div>

        <!-- Señal real confirmada -->
        <div v-else-if="!cargandoEvento && eventoEnVivo && transmisionEnVivo">
          <div class="live-badge mb-3">
            <span class="live-dot"></span>
            EN VIVO
          </div>
          <div class="video-container ratio ratio-16x9 mb-4">
            <iframe
              :src="cloudflareStreamUrl"
              title="Evento en vivo"
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
              allowfullscreen
            ></iframe>
          </div>
          <h6 class="fw-semibold">¡Gracias por acompañarnos!</h6>
          <p class="text-light small">
            Si tienes problemas con la transmisión, actualiza la página o contacta al
            soporte.
          </p>
        </div>

        <!-- Cargando datos del evento -->
        <div v-else class="d-flex justify-content-center py-5">
          <div class="spinner-border text-light" role="status"></div>
        </div>
      </main>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  validarSesionStreaming,
  streamingLogout,
  getEventoActivo,
  getEstadoTransmision,
} from "@/utils/api_evento";
import logoEGPro from "@/assets/img/Evolve/EGPro_logo.jpeg";

const router = useRouter();
const verificando = ref(true);

const cargandoEvento = ref(true);
const eventoData = ref(null);
const eventoEnVivo = ref(true); // fail-open: si algo falla, no bloqueamos el acceso
const eventoDateTime = ref(null); // hora real del evento (para mostrar en el mensaje)
const eventoHabilitaEn = ref(null); // hora real - margen (para habilitar el stream)

const MARGEN_MINUTOS = 10; // el stream se habilita este tiempo antes de la hora oficial

// Cloudflare Stream — Live Input "EGPro6-Evento" (customer subdomain fijo de la cuenta)
const cloudflareStreamUrl = process.env.VUE_APP_CLOUDFLARE_STREAM_URL;

const ahora = ref(new Date());
let intervalId = null;

// Señal real de Cloudflare (distinta de eventoEnVivo, que solo mira el reloj)
const transmisionEnVivo = ref(false);
let pollTransmisionId = null;
const POLL_TRANSMISION_MS = 20000; // 20 segundos

async function revisarTransmision() {
  try {
    const data = await getEstadoTransmision();
    transmisionEnVivo.value = !!data?.en_vivo;
  } catch {
    // Si falla la consulta, nos quedamos mostrando el comercial —
    // no interrumpimos la experiencia por un error de red puntual.
  }

  // Una vez confirmada la señal real, dejamos de preguntar — es un
  // evento de una sola vez, no necesitamos seguir sondeando.
  if (transmisionEnVivo.value && pollTransmisionId) {
    clearInterval(pollTransmisionId);
    pollTransmisionId = null;
  }
}

// ── Sesión ─────────────────────────────────────────────
onMounted(async () => {
  const jti = localStorage.getItem("streaming_jti");

  if (!jti) {
    router.push("/streaming");
    return;
  }

  try {
    const data = await validarSesionStreaming(jti);
    if (!data.valido) {
      limpiarSesion();
      router.push("/streaming");
      return;
    }
  } catch {
    limpiarSesion();
    router.push("/streaming");
    return;
  } finally {
    verificando.value = false;
  }

  await cargarEvento();
});

onUnmounted(() => {
  if (intervalId) clearInterval(intervalId);
  if (pollTransmisionId) clearInterval(pollTransmisionId);
});

function limpiarSesion() {
  localStorage.removeItem("streaming_jti");
  localStorage.removeItem("streaming_expira_en");
  localStorage.removeItem("streaming_email");
}

async function cerrarSesion() {
  const jti = localStorage.getItem("streaming_jti");
  try {
    if (jti) await streamingLogout(jti);
  } catch {
    // Si falla el logout en el server, igual limpiamos local
  } finally {
    limpiarSesion();
    router.push("/streaming");
  }
}

// ── Datos del evento (fecha / hora reales desde el endpoint) ─────────────
async function cargarEvento() {
  cargandoEvento.value = true;
  try {
    const evento = await getEventoActivo();
    eventoData.value = evento;

    if (evento?.fecha_evento) {
      const [year, month, day] = evento.fecha_evento.split("-").map(Number);
      let horas = 0;
      let minutos = 0;
      if (evento.hora_inicio) {
        const [h, m] = evento.hora_inicio.split(":").map(Number);
        horas = h || 0;
        minutos = m || 0;
      }
      eventoDateTime.value = new Date(year, month - 1, day, horas, minutos);
      eventoHabilitaEn.value = new Date(
        eventoDateTime.value.getTime() - MARGEN_MINUTOS * 60 * 1000
      );
      actualizarEstadoEvento();
      intervalId = setInterval(actualizarEstadoEvento, 1000);
    } else {
      // No hay fecha configurada en el back: no bloqueamos el acceso
      eventoEnVivo.value = true;
      iniciarSondeoTransmision();
    }
  } catch (err) {
    console.error("No se pudo obtener el evento activo:", err);
    // Fail-open: si el endpoint falla, dejamos pasar al streaming
    eventoEnVivo.value = true;
    iniciarSondeoTransmision();
  } finally {
    cargandoEvento.value = false;
  }
}

function actualizarEstadoEvento() {
  ahora.value = new Date();
  if (eventoHabilitaEn.value) {
    const yaEnVentana = ahora.value >= eventoHabilitaEn.value;
    if (yaEnVentana && !eventoEnVivo.value) {
      iniciarSondeoTransmision();
    }
    eventoEnVivo.value = yaEnVentana;
    if (eventoEnVivo.value && intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }
}

function iniciarSondeoTransmision() {
  revisarTransmision();
  if (!pollTransmisionId) {
    pollTransmisionId = setInterval(revisarTransmision, POLL_TRANSMISION_MS);
  }
}

// ── Formateo de fecha / hora en español ─────────────────
const fechaFormateada = computed(() => {
  if (!eventoDateTime.value) return "";
  return format(eventoDateTime.value, "EEEE d 'de' MMMM 'de' yyyy", { locale: es });
});

const horaFormateada = computed(() => {
  if (!eventoData.value?.hora_inicio) return "";
  const [h, m] = eventoData.value.hora_inicio.split(":");
  const hora = new Date();
  hora.setHours(Number(h));
  hora.setMinutes(Number(m));
  return format(hora, "hh:mm a", { locale: es });
});

// ── Countdown ─────────────────────────────────────────
const mostrarCountdown = computed(() => !!eventoHabilitaEn.value);

const countdown = computed(() => {
  if (!eventoHabilitaEn.value) {
    return { dias: 0, horas: 0, minutos: 0, segundos: 0 };
  }
  const diffMs = Math.max(0, eventoHabilitaEn.value.getTime() - ahora.value.getTime());
  const totalSegundos = Math.floor(diffMs / 1000);

  const dias = Math.floor(totalSegundos / 86400);
  const horas = Math.floor((totalSegundos % 86400) / 3600);
  const minutos = Math.floor((totalSegundos % 3600) / 60);
  const segundos = totalSegundos % 60;

  return {
    dias: String(dias).padStart(2, "0"),
    horas: String(horas).padStart(2, "0"),
    minutos: String(minutos).padStart(2, "0"),
    segundos: String(segundos).padStart(2, "0"),
  };
});
</script>

<style scoped>
.streaming-wrapper {
  min-height: 100vh;
  background-image: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.1) 0%,
      rgba(0, 0, 0, 0.1) 100%
    ),
    url("@/assets/img/Evolve/JiuJitsu_Background2.png");
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  padding-top: 60px;
  overflow-x: hidden;
}

.custom-header {
  background-color: rgba(0, 0, 0, 0.9);
  border-bottom: 1px solid rgba(204, 0, 0, 0.35);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}

.header-logo {
  height: 52px;
  width: auto;
  object-fit: contain;
  /* Logo con fondo oscuro sobre header oscuro: le damos un halo sutil
     para que no se pierda contra el fondo del header. */
  filter: drop-shadow(0 0 6px rgba(255, 255, 255, 0.08));
}

.content-main {
  margin-top: 3rem;
}

.sitename::before {
  content: "Evolve Grappling Pro";
  color: white;
}

@media (max-width: 576px) {
  .sitename::before {
    content: "EG Pro";
  }
}

/* ── Tarjeta: evento aún no comienza ───────────────────── */
.pre-evento-card {
  max-width: 560px;
  background: rgba(15, 15, 15, 0.72);
  border: 1px solid rgba(204, 0, 0, 0.4);
  border-radius: 18px;
  padding: 2.5rem 2rem;
  backdrop-filter: blur(6px);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.45);
}

.pre-evento-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 1.25rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(
    circle,
    rgba(204, 0, 0, 0.35) 0%,
    rgba(204, 0, 0, 0.08) 70%
  );
  border: 1px solid rgba(204, 0, 0, 0.5);
  font-size: 1.8rem;
  color: #ff4d4d;
}

.text-light-50 {
  color: rgba(255, 255, 255, 0.75);
}

.countdown-grid {
  display: flex;
  justify-content: center;
  gap: 0.9rem;
  margin-top: 1.5rem;
}

.countdown-item {
  min-width: 64px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(166, 166, 166, 0.25);
  border-radius: 10px;
  padding: 0.6rem 0.4rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.countdown-num {
  font-size: 1.6rem;
  font-weight: 700;
  color: #ffffff;
  font-variant-numeric: tabular-nums;
}

.countdown-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.6);
}

/* ── Badge EN VIVO ───────────────────────────────────── */
.live-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(204, 0, 0, 0.15);
  border: 1px solid rgba(204, 0, 0, 0.6);
  color: #ff4d4d;
  font-weight: 700;
  font-size: 0.8rem;
  letter-spacing: 0.08em;
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
}

.live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff2b2b;
  box-shadow: 0 0 0 0 rgba(255, 43, 43, 0.7);
  animation: pulse-dot 1.5s infinite;
}

.badge-espera {
  background: rgba(255, 193, 7, 0.15);
  border-color: rgba(255, 193, 7, 0.6);
  color: #ffc107;
}

.badge-espera .live-dot {
  background: #ffc107;
  box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.7);
}

@keyframes pulse-dot {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 43, 43, 0.7);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(255, 43, 43, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 43, 43, 0);
  }
}

.video-container {
  max-width: 900px;
  margin: 0 auto;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(166, 166, 166, 0.25);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.45);
}

@media (max-width: 576px) {
  .pre-evento-card {
    padding: 2rem 1.25rem;
  }
  .countdown-grid {
    gap: 0.5rem;
  }
  .countdown-item {
    min-width: 54px;
    padding: 0.5rem 0.3rem;
  }
  .countdown-num {
    font-size: 1.3rem;
  }
}
</style>
